import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { createStack } from 'hub-http';
// @ts-expect-error needs types
import { getCookie } from 'hub-http/helpers/cookies';
import promiseClient from 'hub-http/adapters/promiseClient';
import * as core from 'hub-http/middlewares/core';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Text } from 'foundations-components/components/Text';
import useMDXLayout from 'developer-docs-shared-components/hooks/useMDXLayout';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import { articleWidthCss } from '../utils/css';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Form } from 'foundations-components/transitional/components/Form';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const stack = createStack(core.base('https://developers.hubspot.com'), core.jsonBody, core.validateStatus, core.jsonResponse);
const client = promiseClient(stack);

/**
 * Module inspired by:
 *   https://git.hubteam.com/HubSpot/hubspot-developers-website/tree/master/src/modules/DEV%20-%20Doc%20Feedback.module
 * Leverages the endpoint:
 *   https://git.hubteam.com/HubSpot/hubspot-developers-website/blob/master/src/devdoc.functions/submit-feedback.js
 * PROD requests are logged while QA requests are not per:
 *   https://git.hubteam.com/HubSpot/developer-docs-ui/pull/669
 * Responses are posted to:
 *   https://hubspot.slack.com/archives/CNHA0P5SN
 * with data stored in 53 form.
 */

const Aside = styled.aside.withConfig({
  displayName: "DocFeedback__Aside",
  componentId: "plq2h1-0"
})(["@keyframes targetHighlight{0%{background-color:", ";}100%{background-color:", ";}}padding:", ";margin-top:", ";background:", ";&:target{animation:targetHighlight 1000ms ease-out;}", " ", "{article[data-layout='2-column-wide-right'] ~ &{margin-right:32px;}article[data-layout='3-column'] ~ &{", "}}"], ({
  theme
}) => theme.color['fill-accent-yellow-hover'], ({
  theme
}) => theme.color['fill-field-default'], ({
  theme
}) => theme.space[500], ({
  theme
}) => theme.space[1300], ({
  theme
}) => theme.color['fill-field-default'], () => {
  const isMDXLayout = useMDXLayout();
  return isMDXLayout ? articleWidthCss : null;
}, mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), () => articleWidthCss);

/**
 * Shown at the bottom of pages. Used for content review by the tech writers.
 * Responses post to #developer-feedback.
 *
 * @see https://hubspot.enterprise.slack.com/archives/CNHA0P5SN
 */
function DocsFeedback({
  pageId,
  title
}) {
  const [pageHelpful, setPageHelpful] = useState(null);
  const [mainIssue, setMainIssue] = useState(null);
  const [howToImprove, setHowToImprove] = useState('');
  const [permissionToContact, setPermissionToContact] = useState(false);
  const [email, setEmail] = useState('');
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [feedbackSubmissionFailed, setFeedbackSubmissionFailed] = useState(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const isSubmitDisabled = pageHelpful === 'No' && mainIssue === null;
  const hutk = getCookie('hubspotutk', document.cookie);
  const handleSubmit = useCallback(() => {
    setIsFeedbackLoading(true);
    const docURL = window.location.href;
    const fieldValues = [{
      name: 'kb_was_this_page_helpful',
      value: pageHelpful
    }, {
      name: 'kb_how_could_we_improve_this_documentation',
      value: howToImprove
    }, {
      name: 'kb_doc_url',
      value: docURL
    }];
    fieldValues.push({
      name: 'kb_content_id',
      value: `${pageId}`
    });
    if (mainIssue !== null) {
      fieldValues.push({
        name: 'kb_main_docs_issue',
        value: mainIssue
      });
    }
    if (permissionToContact) {
      fieldValues.push({
        name: 'email',
        value: email
      });
      fieldValues.push({
        name: 'kb_allow_doc_feedback_follow_up',
        value: permissionToContact
      });
    }

    // Pass HubSpot UTK if available to avoid 'The cookie needed to link form submissions to existing contacts isn’t being sent.' error on form submission in 53
    const context = {
      pageUri: docURL,
      pageName: title
    };
    if (hutk) {
      context.hutk = hutk;
    }
    client.post('/_hcms/api/devdocs/feedback', {
      data: {
        fields: fieldValues,
        context
      },
      withCredentials: true
    }).then(() => {
      setFeedbackSubmissionFailed(false);
      setHasSubmittedFeedback(true);
    }).catch(e => {
      setFeedbackSubmissionFailed(true);
      console.log(e);
    }).finally(() => {
      setIsFeedbackLoading(false);
    });
  }, [pageHelpful, mainIssue, howToImprove, permissionToContact, email, pageId, title, hutk]);
  return /*#__PURE__*/_jsxs(Aside
  // Needed for links from GitHubEmbed and other failures where feedback is encouraged
  , {
    id: "feedback-form",
    "data-test-id": "feedback-form",
    "data-page-id": pageId,
    "data-title": title
    // Since page has multiple <aside>s, label them: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/complementary_role#labeling_landmarks
    ,
    "aria-label": I18n.text('developer-docs-ui.docFeedback.header'),
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h3",
      typography: "heading-500",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.docFeedback.header"
      })
    }), hasSubmittedFeedback ? /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.docFeedback.thankYou"
      })
    }) : /*#__PURE__*/_jsxs(Form, {
      marginBlockEnd: "400",
      onSubmit: e => {
        e.preventDefault();
        handleSubmit();
      },
      children: [/*#__PURE__*/_jsx(FormControl, {
        required: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-ui.docFeedback.wasHelpful.label"
        }),
        children: /*#__PURE__*/_jsxs(ToggleGroup, {
          name: "page-feedback__sentiment",
          children: [/*#__PURE__*/_jsx(RadioInput, {
            inline: true,
            checked: pageHelpful === 'Yes',
            onChange: () => {
              setMainIssue(null);
              setPageHelpful('Yes');
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.wasHelpful.positive"
            })
          }), /*#__PURE__*/_jsx(RadioInput, {
            inline: true,
            checked: pageHelpful === 'No',
            onChange: () => setPageHelpful('No'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.wasHelpful.negative"
            })
          })]
        })
      }), pageHelpful === 'No' ? /*#__PURE__*/_jsx(FormControl, {
        required: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-ui.docFeedback.mainIssue.label"
        }),
        children: /*#__PURE__*/_jsxs(ToggleGroup, {
          name: "page-main-issue",
          children: [/*#__PURE__*/_jsx(RadioInput, {
            checked: mainIssue === 'Inaccurate',
            onChange: () => setMainIssue('Inaccurate'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.mainIssue.inaccurate"
            })
          }), /*#__PURE__*/_jsx(RadioInput, {
            checked: mainIssue === 'Unclear',
            onChange: () => setMainIssue('Unclear'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.mainIssue.unclear"
            })
          }), /*#__PURE__*/_jsx(RadioInput, {
            checked: mainIssue === 'Missing Information',
            onChange: () => setMainIssue('Missing Information'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.mainIssue.missingInformation"
            })
          }), /*#__PURE__*/_jsx(RadioInput, {
            checked: mainIssue === 'Irrelevant',
            onChange: () => setMainIssue('Irrelevant'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.mainIssue.irrelevant"
            })
          })]
        })
      }) : null, pageHelpful !== null ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          label: pageHelpful === 'Yes' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.docFeedback.howToImprove.positiveSentimentLabel"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.docFeedback.howToImprove.negativeSentimentLabel"
          }),
          children: /*#__PURE__*/_jsx(TextArea, {
            placeholder: I18n.text('developer-docs-ui.docFeedback.howToImprove.placeholder'),
            value: howToImprove,
            onChange: e => setHowToImprove(e.currentTarget.value)
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          children: /*#__PURE__*/_jsx(Checkbox, {
            checked: permissionToContact,
            onChange: e => setPermissionToContact(e.currentTarget.checked),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.permissionToContact.label"
            })
          })
        }), permissionToContact ? /*#__PURE__*/_jsx(FormControl, {
          label: I18n.text('developer-docs-ui.docFeedback.email.label'),
          help: I18n.text('developer-docs-ui.docFeedback.email.help'),
          children: /*#__PURE__*/_jsx(TextInput, {
            value: email,
            type: "email",
            onChange: e => setEmail(e.currentTarget.value)
          })
        }) : null, /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isSubmitDisabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.docFeedback.incompleteFormValidationMessage"
          }),
          children: /*#__PURE__*/_jsx(LoadingButton, {
            use: "primary",
            marginBlockStart: "600",
            disabled: isSubmitDisabled,
            type: "submit",
            loading: isFeedbackLoading,
            failed: feedbackSubmissionFailed,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.docFeedback.submit"
            })
          })
        }), feedbackSubmissionFailed ? /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "400",
          color: "fill-alert-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.docFeedback.error"
          })
        }) : null]
      }) : null]
    }), /*#__PURE__*/_jsx("small", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Link
        },
        options: {
          linkProps: {
            external: true,
            href: 'https://knowledge.hubspot.com/account/get-help-with-hubspot'
          }
        },
        message: "developer-docs-ui.docFeedback.support_jsx"
      })
    })]
  });
}
export default DocsFeedback;